import type { PaletteName } from "./palette";
import { muted, semanticRoles, vivid, type SemanticColor } from "./semantic";

/**
 * The one chart palette (docs/UI_STANDARDS.md). Chart colors come from here or from a semantic role; a page or chart
 * configuration never names a hex value. Series must also be distinguishable without color: pair these with labels,
 * markers, line style, or direct labels.
 */
export const chartRoles = {
  /** The main series on a chart with no positive/negative meaning. */
  primary: semanticRoles.brand,
  /** Favorable series: revenue, growth, uptime, completion. */
  positive: semanticRoles.success,
  /** Unfavorable series, loss, or a breached threshold. */
  negative: semanticRoles.danger,
  /** Exceptions and cautions that are not yet failures. */
  warning: semanticRoles.warning,
  /** Baseline, target, or reference series. */
  baseline: semanticRoles.neutral,
  /** A ranked or distinguished result, only where a real ranking exists. */
  ranking: semanticRoles.achievement,
} as const;
export type ChartRole = keyof typeof chartRoles;

/** Mantine color name for a chart role, for Mantine Charts' `color` props (for example `"brand.7"`). */
export function chartColor(role: ChartRole): SemanticColor {
  return chartRoles[role];
}

/** CSS color for a chart role, for the rare API that cannot take a Mantine color name. */
export function chartCssColor(role: ChartRole): string {
  return role === "baseline" ? muted() : vivid(chartRoles[role]);
}

/**
 * Ordered hues for categorical series with no inherent meaning (for example modules or churches). Semantic roles win
 * whenever the series does carry meaning.
 */
export const categoricalSeries: readonly PaletteName[] = [
  "brand",
  "cyan",
  "violet",
  "teal",
  "orange",
  "indigo",
] as const;
